// Etapa 2 · Saldo de contratos de soporte (ETAPA_0_PROPUESTA §5 y DECISION_FLUJO_REQ §4.1).
//
// Qué consume el contrato (bolsa o baseline):
//  - Todo lo capturado en el proyecto de soporte (INC, SOL, actividades del contrato, estimaciones).
//  - Un REQ cotizado que consume contrato (≤ 40 h) descuenta su TOTAL COTIZADO, no las horas reales:
//    sus horas reales cuentan mes a mes hasta llegar a lo cotizado (lo que pase lo absorbe Ascendix)
//    y, al cerrarse, lo que falte para llegar a lo cotizado se carga en el mes del cierre.
//  - Los REQ de facturación adicional se capturan en su evolutivo: no tocan el contrato.
//
// Funciones puras: sin Firestore, para probarlas sin emulador.
const r2 = (n) => Math.round(n * 100) / 100;
const monthOf = (dateKey) => dateKey.slice(0, 7);
/** Renglones por mes y ticket: lo capturado y lo que cuenta contra el contrato. */
export function chargeLines(entries, reqs) {
    const quoted = new Map(reqs.map(q => [q.number.toUpperCase(), q]));
    const used = new Map();
    const out = new Map();
    const line = (month, ticket) => {
        const key = `${month}|${ticket}`;
        let l = out.get(key);
        if (!l) {
            l = { month, ticket, actual: 0, charged: 0, completion: 0 };
            out.set(key, l);
        }
        return l;
    };
    for (const e of [...entries].sort((a, b) => a.dateKey.localeCompare(b.dateKey))) {
        if (!(e.hours > 0))
            continue;
        const ticket = (e.ticket || '').toUpperCase();
        const l = line(monthOf(e.dateKey), ticket);
        l.actual += e.hours;
        const req = quoted.get(ticket);
        if (!req) {
            l.charged += e.hours;
            continue;
        }
        const before = used.get(ticket) ?? 0;
        l.charged += Math.max(0, Math.min(e.hours, req.quotedHours - before));
        used.set(ticket, before + e.hours);
    }
    for (const req of reqs) {
        if (!req.closedMonth)
            continue;
        const key = req.number.toUpperCase();
        const missing = req.quotedHours - (used.get(key) ?? 0);
        if (missing > 0) {
            const l = line(req.closedMonth, key);
            l.charged += missing;
            l.completion += missing;
        }
    }
    return [...out.values()].map(l => ({ ...l, actual: r2(l.actual), charged: r2(l.charged), completion: r2(l.completion) }));
}
/** Horas que cuentan contra el contrato, por mes. */
export function chargeByMonth(entries, reqs) {
    const out = new Map();
    for (const l of chargeLines(entries, reqs)) {
        let m = out.get(l.month);
        if (!m) {
            m = { month: l.month, charged: 0, actual: 0, reqAdjustment: 0 };
            out.set(l.month, m);
        }
        m.charged += l.charged;
        m.actual += l.actual;
        if (quotedTicket(reqs, l.ticket))
            m.reqAdjustment += l.charged - l.actual;
    }
    for (const m of out.values()) {
        m.charged = r2(m.charged);
        m.actual = r2(m.actual);
        m.reqAdjustment = r2(m.reqAdjustment);
    }
    return out;
}
const quotedTicket = (reqs, ticket) => Boolean(ticket) && reqs.some(r => r.number.toUpperCase() === ticket);
// ---------- ajustes del cierre mensual (ETAPA_0 §5.4) ----------
/** Lo capturado nunca se toca: lo que se carga al cliente se corrige con movimientos. */
export const ADJUSTMENT_KINDS = ['courtesy', 'warranty', 'reclass', 'extra_charge', 'prior_period'];
export const ADJUSTMENT_LABEL = {
    courtesy: 'Cortesía (no se cobra)',
    warranty: 'Garantía',
    reclass: 'Reclasificación',
    extra_charge: 'Cargo extra',
    prior_period: 'Ajuste de periodo anterior'
};
/** Cortesía y garantía siempre restan; cargo extra siempre suma; reclasificación y periodo anterior llevan su signo. */
export function adjustmentEffect(kind, hours) {
    const h = Math.abs(hours);
    if (kind === 'courtesy' || kind === 'warranty')
        return -h;
    if (kind === 'extra_charge')
        return h;
    return hours;
}
/** Aplica los ajustes a las horas cargadas por mes (el saldo, el rollover y la banda se recalculan con esto). */
export function applyAdjustments(charges, adjustments) {
    const out = new Map();
    for (const [k, m] of charges)
        out.set(k, { ...m, adjustments: 0 });
    for (const a of adjustments) {
        let m = out.get(a.month);
        if (!m) {
            m = { month: a.month, charged: 0, actual: 0, reqAdjustment: 0, adjustments: 0 };
            out.set(a.month, m);
        }
        const eff = adjustmentEffect(a.kind, a.hours);
        m.adjustments = r2(m.adjustments + eff);
        m.charged = r2(Math.max(0, m.charged + eff));
    }
    return out;
}
/** Fin de la ventana de aclaración: N días hábiles después de enviar el estado de cuenta (calendario del cliente). */
export const CLARIFICATION_BUSINESS_DAYS = 5;
export const totalCharged = (charges) => r2([...charges.values()].reduce((a, m) => a + m.charged, 0));
/** Bolsa: aviso al 85 % (igual que el tope de Veta), excedida al pasar el 100 %. */
export function bagBalance(maxHours, used) {
    const pct = maxHours > 0 ? used / maxHours : 0;
    return {
        maxHours, used: r2(used), remaining: r2(maxHours - used), pct: Math.round(pct * 1000) / 1000,
        level: pct > 1 ? 'over' : pct >= 0.85 ? 'alert' : 'ok'
    };
}
export function addMonths(month, n) {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(Date.UTC(y, m - 1 + n, 1));
    return d.toISOString().slice(0, 7);
}
export const monthsBetween = (from, to) => {
    const [a, b] = [from.split('-').map(Number), to.split('-').map(Number)];
    return (b[0] - a[0]) * 12 + (b[1] - a[1]);
};
export function baselineStatement(terms, charges, asOfMonth) {
    const base = terms.monthlyHours;
    const band = r2(base * terms.flexBandPct / 100);
    const endMonth = addMonths(terms.startMonth, terms.months - 1);
    const rows = [];
    let carry = 0;
    let advance = 0;
    let poolBand = 0;
    let totalOut = 0;
    let lastMonthAdvance = 0;
    const elapsed = Math.min(terms.months, monthsBetween(terms.startMonth, asOfMonth) + 1);
    for (let i = 0; i < elapsed; i++) {
        const month = addMonths(terms.startMonth, i);
        const available = r2(base + carry - advance);
        const used = charges.get(month)?.charged ?? 0;
        const balance = r2(available - used);
        const carryOut = balance > 0 ? r2(balance * terms.rolloverPct / 100) : 0;
        const excess = Math.max(0, -balance);
        const inBand = r2(Math.min(excess, band));
        const outOfBand = r2(Math.max(0, excess - band));
        rows.push({
            month, index: i + 1, state: month === asOfMonth ? 'current' : 'closed', base, carryIn: carry, advanceIn: advance,
            available, used, balance, carryOut, inBand, outOfBand
        });
        totalOut += outOfBand;
        if (terms.bandSource === 'pool')
            poolBand += inBand;
        advance = terms.bandSource === 'next_month' ? inBand : 0;
        if (terms.bandSource === 'next_month' && i === terms.months - 1)
            lastMonthAdvance = inBand;
        carry = carryOut;
    }
    const current = rows.find(r => r.state === 'current') ?? null;
    let level;
    if (elapsed <= 0)
        level = 'not_started';
    else if (!current)
        level = 'ended';
    else if (current.outOfBand > 0)
        level = 'out';
    else if (current.inBand > 0)
        level = 'band';
    else
        level = current.available > 0 && current.used / current.available >= 0.85 ? 'alert' : 'ok';
    let poolRemaining = null;
    let projectedExhaustMonth = null;
    if (terms.bandSource === 'pool') {
        const monthsLeft = Math.max(0, terms.months - Math.max(0, elapsed));
        poolRemaining = r2(monthsLeft * base - poolBand);
        // Proyección: cada mes por venir consume su base más la banda promedio observada.
        const avgBand = elapsed > 0 ? poolBand / elapsed : 0;
        let pool = poolRemaining;
        if (pool < 0)
            projectedExhaustMonth = asOfMonth;
        else if (avgBand > 0) {
            for (let k = 1; k <= monthsLeft; k++) {
                pool -= base + avgBand;
                if (pool < 0) {
                    projectedExhaustMonth = addMonths(asOfMonth, k);
                    break;
                }
            }
        }
    }
    return {
        terms, rows, current, level, totalInBand: r2(rows.reduce((a, r) => a + r.inBand, 0)), totalOutOfBand: r2(totalOut),
        poolRemaining, projectedExhaustMonth, endMonth, lastMonthAdvance
    };
}
// ---------- horas reales contra cotizadas ----------
export const OVERRUN_PCT = 10;
/** Sobreconsumo: las horas reales pasan lo cotizado en más del 10 % (se alerta al gestor). */
export function quoteOverrun(actual, quoted, thresholdPct = OVERRUN_PCT) {
    const pct = quoted > 0 ? (actual / quoted) * 100 : 0;
    return { actual: r2(actual), quoted, pct: Math.round(pct), over: quoted > 0 && actual > quoted * (1 + thresholdPct / 100) };
}
// ---------- dominios de correo públicos ----------
/** Dominios de correo gratuitos: no pueden identificar a un cliente (cualquiera podría entrar al portal). */
export const PUBLIC_EMAIL_DOMAINS = new Set([
    'gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.es', 'outlook.com', 'outlook.es', 'live.com', 'live.com.mx', 'msn.com',
    'yahoo.com', 'yahoo.com.mx', 'yahoo.es', 'ymail.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com', 'protonmail.com', 'proton.me',
    'gmx.com', 'gmx.net', 'zoho.com', 'mail.com', 'prodigy.net.mx', 'yandex.com', 'qq.com'
]);
export const isPublicEmailDomain = (domain) => PUBLIC_EMAIL_DOMAINS.has(domain.trim().toLowerCase().replace(/^@/, ''));
//# sourceMappingURL=contracts.js.map